import { useMutation, useQueries, useQuery } from '@tanstack/react-query';
import { call } from '@/lib/api';
import { queryClient as qc } from '@/lib/query';
import { ServiceListParams } from '@/types';
import { TAgentService } from './agents';

const callForJson = async <T,>(endpoint: string, json?: any): Promise<T> => {
  return call<T>(endpoint, { json }).then((res) => res.json());
};

const isServiceEnabled = (serviceId: number): boolean => serviceId > 0;

export enum RenewalPeriod {
  None = 0,
  Monthly = 1,
  Quarterly = 2,
  Biannually = 3,
  Annually = 4,
  Biennial = 5,
}

export const RENEWAL_PERIOD_LABELS: Record<RenewalPeriod, string> = {
  [RenewalPeriod.None]: 'None',
  [RenewalPeriod.Monthly]: 'Monthly',
  [RenewalPeriod.Quarterly]: 'Quarterly',
  [RenewalPeriod.Biannually]: 'Biannually',
  [RenewalPeriod.Annually]: 'Annually',
  [RenewalPeriod.Biennial]: 'Biennial',
};

export const RENEWAL_INTERVAL_OPTIONS = Object.entries(RENEWAL_PERIOD_LABELS)
  .filter(([k]) => Number(k) !== RenewalPeriod.None)
  .map(([value, label]) => ({ value, label }));

export const keys = {
  all: ['services'] as const,
  details: (id: number) => [...keys.all, id, 'details'] as const,
  children: (id: number) => [...keys.all, id, 'children'] as const,
  agentServices: (id: number) => [...keys.all, id, 'agentServices'] as const,
  listIncludable: (
    id: number,
    filters?: { jurisdiction?: string; term?: string; exclude?: number[] },
    cursor?: number,
  ) => [...keys.all, id, 'includable', filters, cursor] as const,
  listAll: (
    filters?: { jurisdiction?: string; term?: string; exclude?: number[] },
    cursor?: number,
  ) => [...keys.all, 'all', filters, cursor] as const,
};

const invalidateServiceQueries = (serviceId?: number): void => {
  invalidateLists();

  if (serviceId) {
    qc.invalidateQueries({ queryKey: keys.details(serviceId) });
    qc.invalidateQueries({ queryKey: keys.children(serviceId) });
    qc.invalidateQueries({ queryKey: keys.agentServices(serviceId) });
    invalidateIncludableServices(serviceId);
  }
};

const invalidateLists = () => {
  qc.invalidateQueries({
    queryKey: keys.all,
    predicate: (query) =>
      query.queryKey[0] === 'services' && query.queryKey[1] === 'all',
  });
};

export type TService = {
  id: number;
  parents: number[];
  name: string;
  description: string;
  price: number;
  currency: string;
  jurisdictions: string[];
  children: Omit<TService, 'children'>[];
  standalone: boolean;
  isAdditionalService: boolean;
  /** IS-121: renders an asterisk after the name wherever the service is shown. */
  isMandatory: boolean;
  registersCompany?: boolean;
  invoiceTermsProfile: 'standard' | 'casp_license';
  updatedAt: string;
  type:
    | 'Corporate Services'
    | 'License obtainment assistance'
    | 'Document preparation'
    | 'Legal Services'
    | 'Account opening assistance'
    | 'Reselling Agent Services'
    | 'In-House'
    | 'Nominee Director and Shareholder';
  industry:
    | 'Crypto/Blockchain'
    | 'Payments/Banking'
    | 'Gambling/Gaming'
    | 'Simple Company';
  renewal?: {
    period: RenewalPeriod;
    isRenewable?: boolean;
    renewalPrice?: number;
    offsetDays?: number;
    fineType?: 'static' | 'incremental';
    fineAmount?: number;
    incrementValue?: number;
    incrementInterval?: string;
  };
};

export type TPaginatedServices = {
  services: TService[];
  cursor: number | null;
  total: number;
};

export type TPaginatedServicesResponse = {
  services: TService[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
};

// Hook for service selectors using page-based pagination
export const useServicesFiltered = (
  filters?: {
    jurisdiction?: string;
    strictJurisdiction?: boolean;
    term?: string;
    exclude?: number[];
    currency?: string;
    type?: string;
    minPrice?: number;
    maxPrice?: number;
    serviceFilter?: 'packages' | 'standalone';
    isAdditionalService?: boolean;
  },
  cursor?: number,
  enabled = true,
) => {
  const limit = 50;
  const page = cursor ? Math.floor(cursor / limit) + 1 : 1;

  return useQuery(
    {
      queryKey: keys.listAll(filters, cursor),
      queryFn: async () => {
        const response = await call<TPaginatedServicesResponse>(
          'service/list',
          {
            json: {
              page,
              limit,
              term: filters?.term,
              jurisdiction: filters?.jurisdiction,
              strictJurisdiction: filters?.strictJurisdiction,
              exclude: filters?.exclude,
              currency: filters?.currency,
              type: filters?.type,
              minPrice: filters?.minPrice,
              maxPrice: filters?.maxPrice,
              serviceFilter: filters?.serviceFilter,
              isAdditionalService: filters?.isAdditionalService,
              sortBy: 'updatedAt',
              sortOrder: 'desc',
            } as any,
          },
        ).then((res) => res.json());

        // Transform page-based response to cursor-based format for backward compatibility
        const nextCursor =
          response.page < response.totalPages ? response.page * limit : null;

        return {
          services: response.services,
          cursor: nextCursor,
          total: response.total,
        } as TPaginatedServices;
      },
      enabled,
    },
    qc,
  );
};

// New page-based hook for services list page
export const useServicesList = (params: ServiceListParams) => {
  return useQuery(
    {
      queryKey: [...keys.all, 'paginated', params],
      queryFn: () =>
        call<TPaginatedServicesResponse>('service/list', {
          json: params as any,
        }).then((res) => res.json()),
      enabled: true,
    },
    qc,
  );
};

export const useIncludableServices = (
  serviceId: number,
  filters?: {
    jurisdiction?: string;
    term?: string;
    exclude?: number[];
    currency?: string;
  },
  cursor?: number,
) => {
  return useQuery(
    {
      enabled: isServiceEnabled(serviceId),
      queryKey: keys.listIncludable(serviceId, filters, cursor),
      queryFn: () =>
        callForJson<TPaginatedServices>('service/list-includable', {
          serviceId,
          filters,
          cursor,
        }),
    },
    qc,
  );
};

const invalidateIncludableServices = (parentId: number) => {
  qc.invalidateQueries({
    queryKey: keys.all,
    predicate: (query) =>
      query.queryKey[0] === 'services' &&
      query.queryKey[1] === parentId &&
      query.queryKey[2] === 'includable',
  });
};

export const useSaveService = () => {
  return useMutation(
    {
      mutationFn: (json: TService) => call<void>('service/save', { json }),
      onSuccess: (_, updated) => {
        qc.setQueryData(keys.details(updated.id), (old) => {
          return { ...(old || {}), ...updated };
        });
        updated.parents.map((parentId) => {
          qc.invalidateQueries({ queryKey: keys.details(parentId) });
          qc.invalidateQueries({ queryKey: keys.children(parentId) });
        });
        invalidateLists();
      },
    },
    qc,
  );
};

export const useDeleteService = () => {
  return useMutation(
    {
      mutationFn: (serviceId: number) =>
        call<void>('service/delete', { json: { serviceId } }),
      onSuccess: (_, serviceId) => {
        invalidateServiceQueries(serviceId);
      },
    },
    qc,
  );
};

export const useIncludeService = (parentId: number) => {
  return useMutation(
    {
      mutationFn: (serviceId: number) =>
        call<void>('service/include', { json: { parentId, serviceId } }),
      onSuccess: () => {
        qc.invalidateQueries({ queryKey: keys.details(parentId) });
        qc.invalidateQueries({ queryKey: keys.children(parentId) });
        invalidateIncludableServices(parentId);
        // invalidating all lists, because price changes
        invalidateLists();
      },
    },
    qc,
  );
};

export const useExcludeService = (parentId: number) => {
  return useMutation(
    {
      mutationFn: (serviceId: number) =>
        call<void>('service/exclude', { json: { serviceId } }),
      onSuccess: (_, serviceId) => {
        qc.invalidateQueries({ queryKey: keys.details(serviceId) });
        qc.invalidateQueries({ queryKey: keys.children(parentId) });
        invalidateIncludableServices(parentId);
        // invalidating all lists, because price changes
        invalidateLists();
      },
    },
    qc,
  );
};

export const useReorderChildServices = (serviceId: number) => {
  return useMutation(
    {
      mutationFn: (children: { id: number }[]) =>
        call<void>('service/reorder-children', {
          json: { serviceId, children },
        }),
      onMutate: async (newChildren) => {
        await qc.cancelQueries({ queryKey: keys.children(serviceId) });

        const previousChildren = qc.getQueryData(keys.children(serviceId));

        qc.setQueryData(
          keys.children(serviceId),
          (oldChildren: TChildService[] = []) => {
            const childrenMap = oldChildren.reduce(
              (acc, child) => {
                acc[child.id] = child;
                return acc;
              },
              {} as Record<number, TChildService>,
            );
            return newChildren.map(({ id }) => childrenMap[id]).filter(Boolean);
          },
        );

        return { previousChildren, serviceId };
      },
      onError: (_, __, context) => {
        if (context?.previousChildren) {
          qc.setQueryData(
            keys.children(context.serviceId),
            context.previousChildren,
          );
        }
      },
      onSettled: () => {
        qc.invalidateQueries({ queryKey: keys.children(serviceId) });
      },
    },
    qc,
  );
};

export const useAddService = (parentId?: number) => {
  return useMutation({
    mutationFn: (json: Omit<TService, 'id'>) =>
      callForJson<TService>('service/add', { ...json, parentId }),
    onSuccess: () => {
      invalidateServiceQueries(parentId);
    },
  });
};

export type TChildService = Omit<TService, 'children' | 'parents'> & {
  cogsByCurrency: Record<string, number>;
};

export const useChildServices = (serviceId: number) => {
  return useQuery(
    {
      queryKey: keys.children(serviceId),
      queryFn: () =>
        callForJson<TChildService[]>('service/list-children', { serviceId }),
      enabled: isServiceEnabled(serviceId),
    },
    qc,
  );
};

const createServiceQuery = (id: number) => ({
  queryKey: keys.details(id),
  queryFn: () => callForJson<TService>('service/details', { id }),
  enabled: isServiceEnabled(id),
});

export const useService = (id: number) => useQuery(createServiceQuery(id), qc);

export const useServices = (ids: number[]) =>
  useQueries(
    {
      queries: ids.map(createServiceQuery),
      combine: (results) => ({
        data: results.map((result) => result.data),
        isLoading: results.some((result) => result.isLoading),
        isError: results.some((result) => result.isError),
        errors: results.map((result) => result.error),
      }),
    },
    qc,
  );

export const useIncludedAgentServices = (serviceId: number) => {
  return useQuery<TAgentService[]>(
    {
      queryFn: () =>
        callForJson<TAgentService[]>('agent/services/list', { serviceId }),
      queryKey: keys.agentServices(serviceId),
      select: (data) =>
        data.map((d) => {
          d.updatedAt = new Date(d.updatedAt);
          return d;
        }),
      enabled: isServiceEnabled(serviceId),
    },
    qc,
  );
};

export const useIncludedAgentServicesMulti = (serviceIds: number[]) => {
  return useQueries({
    queries: serviceIds.map((id) => ({
      queryKey: keys.agentServices(id),
      queryFn: () =>
        callForJson<TAgentService[]>('agent/services/list', { serviceId: id }),
      enabled: isServiceEnabled(id),
      staleTime: 1000 * 60 * 5,
    })),
  });
};

export const useIncludeAgentService = (serviceId: number) => {
  return useMutation(
    {
      mutationFn: (agentService: TAgentService) =>
        call<void>('agent/services/include-to-service', {
          json: { serviceId, agentServiceId: agentService.id },
        }),
      onSuccess: (_, includedAgentService) => {
        qc.invalidateQueries({
          queryKey: keys.agentServices(serviceId),
        });
        qc.setQueryData(
          keys.agentServices(serviceId),
          (existing: TAgentService[]) => [...existing, includedAgentService],
        );
      },
    },
    qc,
  );
};

export const useExcludeAgentService = (serviceId: number) => {
  return useMutation(
    {
      mutationFn: (agentService: TAgentService) =>
        call<void>('agent/services/exclude-from-service', {
          json: { serviceId, agentServiceId: agentService.id },
        }),
      onSuccess: (_, excludedAgentService) => {
        qc.invalidateQueries({
          queryKey: keys.agentServices(serviceId),
        });
        qc.setQueryData(
          keys.agentServices(serviceId),
          (existing: TAgentService[]) =>
            existing.filter((e) => e.id !== excludedAgentService.id),
        );
      },
    },
    qc,
  );
};
